import { zodResolver } from "@hookform/resolvers/zod";
import type { InviteDto } from "@ied/shared-types";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useCreateMember } from "@/hooks/mutations/use-create-member";
import { useUpdateInvite } from "@/hooks/mutations/use-update-invite";
import { Button } from "../ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const FormSchema = z.object({
	first_name: z.string().nonempty(),
	last_name: z.string().nonempty(),
	email: z.email(),
	phone: z.string().refine(
		(val) => {
			let phoneNumber = parsePhoneNumberFromString(val);
			if (!phoneNumber) {
				phoneNumber = parsePhoneNumberFromString(val, "US");
			}
			return phoneNumber?.isValid() ?? false;
		},
		{ message: "Invalid phone number" },
	),
});

type RegisterFormType = z.infer<typeof FormSchema>;

type Props = {
	onSuccess: () => void;
	onError: () => void;
	invite: InviteDto | undefined;
};

export function RegisterForm({ onSuccess, onError, invite }: Props) {
	const { mutate } = useCreateMember();
	const { mutate: updateInvite } = useUpdateInvite();
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<RegisterFormType>({
		defaultValues: {
			first_name: "",
			last_name: "",
			email: "",
			phone: "",
		},
		resolver: zodResolver(FormSchema),
	});

	const onSubmit: SubmitHandler<RegisterFormType> = (data) => {
		if (!invite) {
			onError();
			return;
		}

		// First, try to mark the invite as accepted
		updateInvite(
			{ accepted: true, inviteCode: invite.invite_code },
			{
				onError,
				onSuccess: () => {
					// Only create the member if the invite was successfully accepted
					mutate(
						{
							...data,
							groups: invite.groups ?? null,
						},
						{
							onSuccess,
							onError,
						},
					);
				},
			},
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Card className="relative w-full max-w-sm overflow-hidden pt-0">
				<div className="bg-primary absolute inset-0 z-30 aspect-video opacity-50 mix-blend-color" />
				<img
					src="https://images.unsplash.com/photo-1604076850742-4c7221f3101b?q=80&w=1887&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					className="relative z-20 aspect-video w-full object-cover brightness-60 grayscale"
				/>
				<CardHeader className="space-y-4">
					<CardTitle className="text-lg">
						Welcome to INDIVISIBLE El Dorado
					</CardTitle>
				</CardHeader>
				<CardDescription className="px-4 space-y-1">
					<p className="text-base">Thank you for volunteering!</p>
					<p>Please provide the requested information for access to:</p>
					<ul>
						<li>- group chats</li>
						<li>- email distribution lists</li>
						<li>- shared cloud drive resources</li>
					</ul>
					<p>
						If you do not wish to share this information please contact us
						directly at:{" "}
						<a
							className="underline text-blue-500"
							href="mailto:reachout@indivisibleeldorado.org"
						>
							reachout@indivisibleeldorado.org
						</a>
					</p>
					<div className="space-y-4 mt-6">
						<div className="flex flex-wrap justify-between gap-y-4">
							<div className="w-full space-y-2">
								<Label className="font-bold" htmlFor="first">
									First Name
								</Label>
								<Input {...register("first_name")} id="first" autoFocus />
								{errors.first_name && (
									<p className="text-red-500 text-xs">
										{errors.first_name.message}
									</p>
								)}
							</div>
							<div className="w-full space-y-2">
								<Label className="font-bold" htmlFor="last">
									Last Name
								</Label>
								<Input {...register("last_name")} id="last" />
								{errors.last_name && (
									<p className="text-red-500 text-xs">
										{errors.last_name.message}
									</p>
								)}
							</div>
						</div>
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="email">
								Email
							</Label>
							<Input {...register("email")} type="email" id="email" />
							{errors.email && (
								<p className="text-red-500 text-xs">{errors.email.message}</p>
							)}
						</div>
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="phone">
								Phone Number
							</Label>
							<Input {...register("phone")} type="tel" id="phone" />
							{errors.phone && (
								<p className="text-red-500 text-xs">{errors.phone.message}</p>
							)}
						</div>
					</div>
				</CardDescription>
				<CardFooter className="flex justify-between">
					<Button type="submit">Register</Button>
				</CardFooter>
			</Card>
		</form>
	);
}
